import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFormat } from '../../context/FormatContext';
import GrammarAnalysisDrawer from './GrammarAnalysisDrawer';

const {
  FiActivity, FiAlertTriangle, FiCheckCircle, FiTool, FiArrowRight,
  FiBookOpen, FiClock, FiFileText, FiType, FiEye, FiInfo, FiSparkles, FiCheck, FiRefreshCw,
  FiSearch
} = FiIcons;

const AnalysisStep = () => {
  const {
    pipelineState, autoFixIssues, proceedToStructure, loading, activeProject, retryAnalysis
  } = useFormat();
  const [showEditorialDetails, setShowEditorialDetails] = useState(false);
  const [showGrammarDrawer, setShowGrammarDrawer] = useState(false);

  if (!pipelineState?.analysis) {
    const analysisError = pipelineState?.analysisError;
    return (
      <div className="max-w-4xl mx-auto py-16 text-center">
        {analysisError ? (
          <div className="max-w-xl mx-auto rounded-2xl border border-amber-200 bg-amber-50 p-6">
            <SafeIcon icon={FiAlertTriangle} className="text-3xl text-amber-600 mx-auto mb-3" />
            <p className="text-slate-700 font-semibold">{analysisError}</p>
            <button
              type="button"
              onClick={retryAnalysis}
              disabled={loading}
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-white disabled:opacity-50"
            >
              <SafeIcon icon={FiRefreshCw} />
              Retry Analysis
            </button>
          </div>
        ) : (
          <>
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-500 font-medium text-lg">Running Smart Manuscript & Editorial Analysis...</p>
            <p className="text-slate-400 text-xs mt-1">Checking heading styles, scene breaks, and typographic consistency.</p>
          </>
        )}
      </div>
    );
  }

  const { score, report = {} } = pipelineState.analysis;

  const getScoreColor = (scoreVal) => {
    if (scoreVal >= 85) return 'text-emerald-500';
    if (scoreVal >= 65) return 'text-amber-500';
    return 'text-rose-500';
  };

  const getScoreBadge = (scoreVal) => {
    if (scoreVal >= 85) return { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Publication Ready' };
    if (scoreVal >= 65) return { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Action Recommended' };
    return { bg: 'bg-rose-50', text: 'text-rose-700', label: 'Formatting Needed' };
  };

  const hasIssues = report.warnings && report.warnings.length > 0;
  const editorial = report.editorial || {};
  const stats = report.stats || {};
  const suggestions = report.suggestions || [];
  const badge = getScoreBadge(score);
  const draftFile = pipelineState.file instanceof File ? pipelineState.file : null;
  const hasSavedId = Boolean(activeProject?.id && String(activeProject.id) !== 'new');
  const canRunGrammarCheck = hasSavedId || Boolean(draftFile);

  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="max-w-5xl mx-auto py-6">
      
      {/* Header */}
      <div className="text-center mb-8">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold uppercase tracking-wider mb-2">
          <SafeIcon icon={FiSparkles} className="text-blue-500" />
          FormatterPro Intelligence
        </span>
        <h2 className="text-3xl font-serif font-bold text-slate-800 mb-2">Manuscript & Editorial Analysis</h2>
        <p className="text-slate-500 text-sm max-w-xl mx-auto">
          We analyzed your document structure, ornamental scene transitions, typography, and editorial consistency.
        </p>
      </div>

      {/* Primary Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        
        {/* Health Score */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col items-center justify-center text-center">
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Health Score</div>
          <div className={`text-4xl font-black ${getScoreColor(score)}`}>{score}<span className="text-xl text-slate-400 font-normal">/100</span></div>
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full mt-1.5 ${badge.bg} ${badge.text}`}>
            {badge.label}
          </span>
        </div>

        {/* Chapters Detected */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col items-center justify-center text-center">
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Chapters Detected</div>
          <div className="text-4xl font-serif font-bold text-slate-800">{report.chapters_detected ?? 0}</div>
          <span className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
            <SafeIcon icon={FiBookOpen} className="text-slate-400 text-xs" />
            From Heading 1 / Main Headings / Page &amp; Section Breaks
          </span>
        </div>

        {/* Scene Breaks */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col items-center justify-center text-center">
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Scene Breaks</div>
          <div className="text-4xl font-serif font-bold text-slate-800">{report.scene_breaks_detected ?? 0}</div>
          <span className="text-[11px] text-slate-500 mt-1">
            *** or ornament glyphs
          </span>
        </div>

        {/* Word Count & Reading Time */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col items-center justify-center text-center">
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Estimated Words</div>
          <div className="text-3xl font-serif font-bold text-slate-800">
            {stats.total_words ? stats.total_words.toLocaleString() : '—'}
          </div>
          <span className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
            <SafeIcon icon={FiClock} className="text-slate-400 text-xs" />
            ~{stats.reading_time_mins || 0} min reading time
          </span>
        </div>

      </div>

      {/* Editorial & Proofreading Flags Section */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-6 overflow-hidden">
        <div className="bg-slate-50/80 px-6 py-3.5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiFileText} className="text-primary text-base" />
            <h3 className="font-bold text-slate-800 text-sm">Editorial & Proofreading Scan</h3>
          </div>
          <button 
            type="button" 
            onClick={() => setShowEditorialDetails(!showEditorialDetails)}
            className="text-xs font-semibold text-primary hover:text-blue-800 transition-colors flex items-center gap-1"
          >
            <SafeIcon icon={FiEye} />
            {showEditorialDetails ? 'Hide Details' : 'View Details'}
          </button>
          <button
            type="button"
            onClick={() => setShowGrammarDrawer(true)}
            disabled={!canRunGrammarCheck}
            className="text-xs font-semibold text-primary hover:text-blue-800 transition-colors flex items-center gap-1 disabled:opacity-50"
          >
            <SafeIcon icon={FiSearch} />
            Check spelling &amp; grammar
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            
            {/* Repeated words check */}
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/70">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Repeated Words</div>
              <div className="text-2xl font-bold text-slate-800">
                {editorial.repeated_words_count ?? 0}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {editorial.repeated_words_count > 0 
                  ? `e.g., "${editorial.repeated_words_list?.slice(0, 3).join('", "')}"`
                  : 'No accidental double words found'}
              </p>
            </div>

            {/* Smart quotes vs Straight Quotes */}
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/70">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Typographic Quotes</div>
              <div className="text-2xl font-bold text-slate-800">
                {editorial.straight_quotes_count > 0 ? `${editorial.straight_quotes_count}` : 'Smart Quotes'}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {editorial.straight_quotes_count > 0 
                  ? 'Straight typewriter quotes (auto-converted to curly)'
                  : 'Curled curly quotes intact'}
              </p>
            </div>

            {/* Dialogue Density */}
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/70">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Dialogue Ratio</div>
              <div className="text-2xl font-bold text-slate-800">
                {stats.dialogue_percentage ?? 0}%
              </div>
              <p className="text-xs text-slate-500 mt-1">Estimated spoken dialogue proportion</p>
            </div>

          </div>

          {showEditorialDetails && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-5 pt-4 border-t border-slate-200">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-3">Typographic Diagnostics</h4>
              <ul className="space-y-2 text-xs text-slate-600">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                  <span><strong>Double Spaces:</strong> {editorial.double_spaces_count || 0} instances detected (automatically normalized in final export).</span>
                </li>
                {editorial.spelling_grammar_flags?.map((flag, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                    <span>{flag}</span>
                  </li>
                ))}
                {report.detected_chapter_titles && report.detected_chapter_titles.length > 0 && (
                  <li className="flex items-start gap-2 pt-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1"></span>
                    <span><strong>Sample Chapter Headings Found:</strong> {report.detected_chapter_titles.slice(0, 5).join(' • ')}</span>
                  </li>
                )}
              </ul>
            </motion.div>
          )}

        </div>
      </div>

      {/* Diagnostic & Warnings Section */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-8 overflow-hidden">
        <div className="bg-slate-50/80 px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center">
            <SafeIcon
              icon={hasIssues ? FiAlertTriangle : FiCheckCircle}
              className={`text-xl mr-3 ${hasIssues ? 'text-amber-500' : 'text-emerald-500'}`}
            />
            <h3 className="font-bold text-slate-800">Diagnostic & Layout Findings</h3>
          </div>
          {hasIssues && (
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800">
              {report.warnings.length} {report.warnings.length === 1 ? 'Notice' : 'Notices'}
            </span>
          )}
        </div>

        <div className="p-6">
          {hasIssues ? (
            <div className="space-y-3">
              {report.warnings.map((warning, idx) => (
                <div key={idx} className="flex items-start p-3.5 bg-amber-50/70 border border-amber-200/80 rounded-xl">
                  <SafeIcon icon={FiAlertTriangle} className="text-amber-500 mt-0.5 mr-3 flex-shrink-0 text-base" />
                  <p className="text-xs sm:text-sm text-amber-900 font-medium leading-relaxed">{warning}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <SafeIcon icon={FiCheckCircle} className="text-5xl text-emerald-500 mx-auto mb-3" />
              <h4 className="text-lg font-bold text-slate-800 mb-1">Manuscript Structure Looks Great!</h4>
              <p className="text-slate-500 text-sm max-w-md mx-auto">
                No major structural blockers detected. You can safely map your front and back matter in the next step.
              </p>
            </div>
          )}

          {suggestions.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
              <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Formatter Suggestions:</div>
              {suggestions.map((sug, i) => (
                <p key={i} className="text-xs text-slate-600 flex items-center gap-2">
                  <SafeIcon icon={FiInfo} className="text-blue-500 flex-shrink-0" />
                  <span>{sug}</span>
                </p>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        {hasIssues ? (
          <button
            type="button"
            onClick={autoFixIssues}
            disabled={loading}
            className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-white px-7 py-3.5 rounded-xl font-bold uppercase tracking-wider text-xs transition-all shadow-md hover:shadow-lg flex items-center justify-center disabled:opacity-50"
          >
            {loading ? (
              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2.5"></div><span>Cleaning Up...</span></>
            ) : (
              <><SafeIcon icon={FiTool} className="mr-2 text-sm" /> Auto-Fix Formatting Issues</>
            )}
          </button>
        ) : (
          <div />
        )}

        <button
          type="button"
          onClick={proceedToStructure}
          disabled={loading || !pipelineState.structure}
          className="w-full sm:w-auto bg-primary hover:bg-blue-800 text-white px-8 py-3.5 rounded-xl font-bold uppercase tracking-wider text-xs transition-all shadow-md hover:shadow-lg flex items-center justify-center disabled:opacity-50"
        >
          <span>Continue to Structure</span>
          <SafeIcon icon={FiArrowRight} className="ml-2 text-sm" />
        </button>
      </div>

      {showGrammarDrawer && (
        <GrammarAnalysisDrawer
          manuscriptId={activeProject?.id}
          manuscriptFile={draftFile}
          manuscriptContent={pipelineState.manuscriptContent}
          manuscriptTitle={pipelineState.metadata?.title}
          onClose={() => setShowGrammarDrawer(false)}
        />
      )}
    </motion.div>
  );
};

export default AnalysisStep;